/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    lifecycleStageId,
    assigneeId,
    conversationType,
    sortBy,
    updatedWithin,
    contactQuery,
    assigneeAgentBotId,
    unrepliedOnly,
    signal,
  }) {
    return axios.get(this.url, {
      signal,
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        lifecycle_stage_id: lifecycleStageId,
        assignee_id: assigneeId,
        sort_by: sortBy,
        updated_within: updatedWithin,
        contact_query: contactQuery,
        assignee_agent_bot_id: assigneeAgentBotId,
        unreplied_only: unrepliedOnly,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      signal: payload.signal,
      params: {
        page: payload.page,
        contact_query: payload.contactQuery,
        assignee_agent_bot_id: payload.assigneeAgentBotId,
        unreplied_only: payload.unrepliedOnly,
      },
    });
  }

  filterOptions() {
    return axios.get(`${this.url}/filter_options`);
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    closingNote = null,
  }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
      closing_note: closingNote,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId, assigneeType = null }) {
    const params = {
      assignee_id: agentId,
    };
    if (assigneeType) {
      params.assignee_type = assigneeType;
    }
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  cancelAiFollowUp(conversationId, followUpId) {
    return axios.post(`${this.url}/${conversationId}/cancel_ai_follow_up`, {
      follow_up_id: followUpId,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    includeSidebarBreakdown,
    includeCounts,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        include_sidebar_breakdown: includeSidebarBreakdown,
        include_counts: includeCounts,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
